import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Skeleton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { address, contactNumber, names } from "../../../../constants/Constants";
import LienHoldersStyles from "./LienHoldersStyles";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { RootState } from "../../../../store/store";
import CustomTable from "../../../../common/CustomTable/CustomTable";
import { useParams } from "react-router-dom";
import { ILienHolder } from "../../../../models/user/policyDetails/PolicyDetails";
import { GetLienHolders } from "../../../../store/user/policyDetails/policyDetailsActions";
import { useTranslation } from "react-i18next";
import { isFeatureAvailable } from "../../../../utils/Utility";
import AddLienHolderForm from "./AddLienHolderForm/AddLienHolderForm";
import CustomDrawer from "../../../../common/CustomDrawer/CustomDrawer";
import { setCalledReducerType } from "../../../../store/common/CommonSlice";
import CustomSkeleton from "../../../../common/CustomSkeleton/CustomSkeleton";

const LienHolders: React.FC = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation();
  const { features } = useAppSelector((state: RootState) => state.features);

  const [addLienHolderFormOpen, setAddLienHolderFormOpen] =
    useState<boolean>(false);

  const { policyDetailsHeader } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  const { policyId, carrier, system, lobCode } = useParams();

  const { calledReducerType, isLoading } = useAppSelector(
    (store: RootState) => store.common
  );

  const { lienHolders } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  const handleAddLienHolder = useCallback(() => {
    dispatch(setCalledReducerType(null));
    setAddLienHolderFormOpen(true);
  }, []);

  const columns = [
    {
      label:
        lobCode && lobCode === "PA"
          ? t("Vehicle Description")
          : t("Loan Number"),
      accessorKey: lobCode && lobCode === "PA" ? "riskName" : "loanNumber",
      Cell: (lienHolder: any) => {
        return lobCode && lobCode === "PA"
          ? lienHolder.riskName
            ? `${lienHolder.riskName}`
            : "-"
          : lienHolder.loanNumber
          ? `${lienHolder.loanNumber}`
          : "-";
      },
    },
    {
      label: t("Name"),
      accessorKey: "interestName",
    },
    {
      label: t("Address"),
      accessorKey: "address",
      Cell: (lienHolder: any) => {
        return lienHolder.address
          ? `${lienHolder.address.addr1} ${lienHolder.address.city} ${lienHolder.address.stateProvCd} ${lienHolder.address.regionISOCd} ${lienHolder.address.postalCode}`
          : "-";
      },
    },
    {
      label: t("Contact Number"),
      accessorKey: "phoneNumber",
      Cell: (lienHolder: any) => {
        return lienHolder.phoneNumber ? `${lienHolder.phoneNumber}` : "-";
      },
    },
  ];

  const ResponsiveLienHoldersTabComponent: React.FC = () => {
    return (
      <Box 
        sx={LienHoldersStyles.leinholderMobileContainer}
        id="lienholder_mobile_list"
        data-test-id="lienholder_mobile_list"
        aria-label="Lienholder mobile list"
      >
        {lienHolders.length > 0 ? (
          lienHolders.map((lienHolder: ILienHolder, index: number) => (
            <Box 
              sx={LienHoldersStyles.container} 
              key={index}
              id={`lienholder_mobile_item_${index}`}
              data-test-id={`lienholder_mobile_item_${index}`}
              aria-label={`Lienholder ${index + 1} details`}
            >
              {lobCode && lobCode === "HO" ? (
                <Box 
                  sx={LienHoldersStyles.cardContent}
                  id={`lienholder_loan_number_${index}`}
                  data-test-id={`lienholder_loan_number_${index}`}
                  aria-label="Loan number information"
                >
                  <Typography 
                    sx={LienHoldersStyles.descriptionText}
                    id={`lienholder_loan_number_label_${index}`}
                    data-test-id={`lienholder_loan_number_label_${index}`}
                    aria-label="Loan number label"
                  >
                    {t("Loan Number")}
                  </Typography>
                  <Typography 
                    sx={LienHoldersStyles.contentText}
                    id={`lienholder_loan_number_value_${index}`}
                    data-test-id={`lienholder_loan_number_value_${index}`}
                    aria-label={`Loan number: ${lienHolder.loanNumber || '-'}`}
                  >
                    {lienHolder.loanNumber ? lienHolder.loanNumber : "-"}
                  </Typography>
                </Box>
              ) : (
                <Box 
                  sx={LienHoldersStyles.cardContent}
                  id={`lienholder_vehicle_${index}`}
                  data-test-id={`lienholder_vehicle_${index}`}
                  aria-label="Vehicle description information"
                >
                  <Typography 
                    sx={LienHoldersStyles.descriptionText}
                    id={`lienholder_vehicle_label_${index}`}
                    data-test-id={`lienholder_vehicle_label_${index}`}
                    aria-label="Vehicle description label"
                  >
                    {t("Vehicle Description")}
                  </Typography>
                  <Typography 
                    sx={LienHoldersStyles.contentText}
                    id={`lienholder_vehicle_value_${index}`}
                    data-test-id={`lienholder_vehicle_value_${index}`}
                    aria-label={`Vehicle description: ${lienHolder.riskName || '-'}`}
                  >
                    {lienHolder.riskName ? lienHolder.riskName : "-"}
                  </Typography>
                </Box>
              )}
              <Box 
                sx={LienHoldersStyles.cardContent}
                id={`lienholder_name_${index}`}
                data-test-id={`lienholder_name_${index}`}
                aria-label="Name information"
              >
                <Typography 
                  sx={LienHoldersStyles.descriptionText}
                  id={`lienholder_name_label_${index}`}
                  data-test-id={`lienholder_name_label_${index}`}
                  aria-label="Name label"
                >
                  {names}
                </Typography>
                <Typography 
                  sx={LienHoldersStyles.contentText}
                  id={`lienholder_name_value_${index}`}
                  data-test-id={`lienholder_name_value_${index}`}
                  aria-label={`Name: ${lienHolder.interestName}`}
                >
                  {lienHolder.interestName}
                </Typography>
              </Box>
              <Box 
                sx={LienHoldersStyles.cardContent}
                id={`lienholder_address_${index}`}
                data-test-id={`lienholder_address_${index}`}
                aria-label="Address information"
              >
                <Typography 
                  sx={LienHoldersStyles.descriptionText}
                  id={`lienholder_address_label_${index}`}
                  data-test-id={`lienholder_address_label_${index}`}
                  aria-label="Address label"
                >
                  {address}
                </Typography>
                <Typography 
                  sx={LienHoldersStyles.contentText}
                  id={`lienholder_address_value_${index}`}
                  data-test-id={`lienholder_address_value_${index}`}
                  aria-label={`Address: ${lienHolder.address ? `${lienHolder.address.addr1} ${lienHolder.address.city} ${lienHolder.address.stateProvCd} ${lienHolder.address.regionISOCd} ${lienHolder.address.postalCode}` : '-'}`}
                >
                  {`${lienHolder.address.addr1} ${lienHolder.address.city} ${lienHolder.address.stateProvCd} ${lienHolder.address.regionISOCd} ${lienHolder.address.postalCode}`}
                </Typography>
              </Box>
              <Box 
                sx={LienHoldersStyles.cardContent}
                id={`lienholder_contact_${index}`}
                data-test-id={`lienholder_contact_${index}`}
                aria-label="Contact number information"
              >
                <Typography 
                  sx={LienHoldersStyles.descriptionText}
                  id={`lienholder_contact_label_${index}`}
                  data-test-id={`lienholder_contact_label_${index}`}
                  aria-label="Contact number label"
                >
                  {contactNumber}
                </Typography>
                <Typography 
                  sx={LienHoldersStyles.contentText}
                  id={`lienholder_contact_value_${index}`}
                  data-test-id={`lienholder_contact_value_${index}`}
                  aria-label={`Contact number: ${lienHolder.phoneNumber || '-'}`}
                >
                  {lienHolder &&
                  lienHolder.phoneNumber &&
                  lienHolder.phoneNumber.length > 0
                    ? lienHolder.phoneNumber
                    : "-"}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Card 
            sx={LienHoldersStyles.noLienHoldersCard}
            id="lienholder_no_results"
            data-test-id="lienholder_no_results"
            aria-label="No lienholders found"
          >
            <Typography 
              sx={LienHoldersStyles.noLienHoldersText}
              id="lienholder_no_results_text"
              data-test-id="lienholder_no_results_text"
              aria-label="No records found message"
            >
              {t("No Records Found")}
            </Typography>
          </Card>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (policyId && lobCode && carrier && system) {
      dispatch(GetLienHolders({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, dispatch, lobCode, carrier, system]);

  const isMobile = useMediaQuery("(max-width:600px)");

  return (
    <Box
      aria-label="Lienholder container"
      sx={LienHoldersStyles.leinholderContainer}
      id="lienholder_container"
      data-test-id="lienholder_container"
    >
      <Box 
        sx={LienHoldersStyles.headingAndButton}
        id="lienholder_header"
        data-test-id="lienholder_header"
        aria-label="Lienholder header section"
      >
        {isFeatureAvailable({
          featureCodes: ["lob_pa_policies_add_lienholder"],
          features,
        }) &&
        policyDetailsHeader?.lobCode === "PA" &&
        policyDetailsHeader?.carrierPolicyStatus !== "Cancelled" ? (
          <Button
            id="add_lienholder_button"
            data-test-id="add_lienholder_button"
            aria-label="Add Lienholder"
            variant="contained"
            sx={LienHoldersStyles.addLienHolderButton}
            onClick={handleAddLienHolder}
          >
            {t("Add Lienholder")}
          </Button>
        ) : (
          <></>
        )}
      </Box>
      {!isMobile ? (
        <CustomTable
          columns={columns}
          data={lienHolders}
          displaySkeleton={
            isLoading && calledReducerType === "policyDetails/GetLienHolders"
          }
        />
      ) : (
        <Box 
          sx={LienHoldersStyles.cardContainer}
          id="lienholder_mobile_container"
          data-test-id="lienholder_mobile_container"
          aria-label="Lienholder mobile view"
        >
          {isLoading && calledReducerType === "policyDetails/GetLienHolders" ? (
            <CustomSkeleton 
              height={180} 
              count={2} 
              direction="column"
            />
          ) : (
            <ResponsiveLienHoldersTabComponent />
          )}
        </Box>
      )}

      <CustomDrawer
        open={addLienHolderFormOpen}
        onClose={() => setAddLienHolderFormOpen(false)}
        title={t("Add Lienholder")}
        content={
          <AddLienHolderForm
            open={addLienHolderFormOpen}
            setOpen={setAddLienHolderFormOpen}
          />
        }
        contentStyles={{ padding: 0 }}
      />
    </Box>
  );
};

export default LienHolders;
